// Paleta, formateo de moneda y zona horaria del panel.
//
// Vivían dentro de App.js, pero al partir el panel en secciones (Live Funnel,
// Gateway Health, SMS…) hacían falta desde varios ficheros. Se extraen aquí para
// que haya UNA sola fuente de verdad: si la paleta o la divisa divergen entre
// App.js y una sección, la interfaz se ve rota de formas sutiles.

export const G = {
  black: '#06050A',
  card: '#0D0B14',
  cardHover: '#12101A',
  border: '#1E1A2E',
  borderGold: '#C9A84C40',
  gold: '#C9A84C',
  goldLight: '#E8C96A',
  goldBright: '#F5D98A',
  goldDim: '#C9A84C18',
  goldGlow: '#C9A84C30',
  white: '#F0EDE8',
  muted: '#6B6580',
  dim: '#9990B8',
  red: '#E84B4B',
  redDim: '#E84B4B18',
  green: '#4BE8A0',
  greenDim: '#4BE8A018',
  blue: '#4B9FE8',
  blueDim: '#4B9FE818',
  orange: '#E8994B',
  orangeDim: '#E8994B18',
  surface: '#16132A',
};

// Símbolo de moneda por código ISO. Las tablas mostraban "€" fijo aunque el
// tenant cobrase en otra divisa (p.ej. Carlos en GBP) — engañoso.
export function curSym(c) {
  return ({ EUR: '€', USD: '$', GBP: '£' })[String(c || '').toUpperCase()] || (c ? c + ' ' : '€');
}

// ── DIVISA DE VISUALIZACIÓN ────────────────────────────────────────────────
// Toggle del operador (EUR por defecto, USD para contabilidad en EEUU). Es
// SOLO presentación: los cobros siguen persistiéndose en EUR.
//
// Es un objeto a nivel de módulo (no Context) porque decenas de componentes
// muestran dinero, y cambiar la divisa recarga la página a propósito (todos los
// números cambian a la vez, sin parpadeo), así que no hace falta reactividad.
const _currencyStore = {
  displayCurrency: 'EUR',
  eurToUsdRate: 1,
};

export function _initCurrency({ displayCurrency, eurToUsdRate }) {
  if (displayCurrency === 'EUR' || displayCurrency === 'USD') {
    _currencyStore.displayCurrency = displayCurrency;
  }
  if (typeof eurToUsdRate === 'number' && isFinite(eurToUsdRate) && eurToUsdRate > 0) {
    _currencyStore.eurToUsdRate = eurToUsdRate;
  }
}

// fmt(importeEnEUR) — formateador canónico. TODO el dinero se guarda y calcula
// en EUR; este helper es el único punto donde un importe guardado se convierte
// en texto. Por defecto "€12.99"; con { suffix: true } da "12.99€".
export function fmt(amountEur, opts = {}) {
  const { decimals = 2, suffix = false } = opts;
  const n = typeof amountEur === 'number' ? amountEur : parseFloat(amountEur);
  if (!isFinite(n)) return '—';
  const isUsd = _currencyStore.displayCurrency === 'USD';
  const value = isUsd ? n * _currencyStore.eurToUsdRate : n;
  const sym = isUsd ? '$' : '€';
  const s = value.toFixed(decimals);
  return suffix ? `${s}${sym}` : `${sym}${s}`;
}

export function fmtSymbol() {
  return _currencyStore.displayCurrency === 'USD' ? '$' : '€';
}

export function fmtCode() {
  return _currencyStore.displayCurrency;
}

// Compacto "1.2k€" para ejes de gráficas.
export function fmtK(amountEur) {
  const n = typeof amountEur === 'number' ? amountEur : parseFloat(amountEur);
  if (!isFinite(n)) return '0' + fmtSymbol();
  const isUsd = _currencyStore.displayCurrency === 'USD';
  const val = isUsd ? n * _currencyStore.eurToUsdRate : n;
  const sym = isUsd ? '$' : '€';
  if (val >= 1000) return (val / 1000).toFixed(val >= 10000 ? 0 : 1) + 'k' + sym;
  return val.toFixed(0) + sym;
}

// ── ZONA HORARIA ───────────────────────────────────────────────────────────
// App.js mantiene su propia APP_TZ mutable (la reasigna al sincronizar con el
// backend). Las secciones nuevas no necesitan esa variable viva: les basta el
// valor actual, que siempre está en localStorage. Leerlo aquí evita tener que
// tocar los ~70 usos de APP_TZ que ya hay en App.js.
export function getAppTz() {
  try { return localStorage.getItem('app_tz') || 'America/New_York'; } catch { return 'America/New_York'; }
}

// Fecha corta en la zona del panel. Devuelve '--' si no hay fecha, para que las
// tablas no muestren "Invalid Date".
export function fmtDay(iso) {
  if (!iso) return '--';
  try { return new Date(iso).toLocaleDateString('es-ES', { timeZone: getAppTz() }); } catch { return '--'; }
}

// Fecha + hora en dos líneas lógicas (devuelve { day, time }).
export function fmtDayTime(iso) {
  if (!iso) return { day: '--', time: '' };
  try {
    const d = new Date(iso);
    const tz = getAppTz();
    return {
      day: d.toLocaleDateString('es-ES', { timeZone: tz }),
      time: d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', timeZone: tz }),
    };
  } catch { return { day: '--', time: '' }; }
}
